import type { RawAxiosRequestConfig } from 'axios'
import globalAxios from 'axios'
import { DateTime } from 'luxon'
import type {
  ContainerType,
  GETTimeBucketAppointmentStatusCount,
  PortComplex,
  ShippingLine,
  TerminalName,
} from './apiClient'
import {
  AppointmentsApi,
  ContainersApi,
  ContainerWatchState,
  SortOptions,
  TransactionDirection,
} from './apiClient'
import type { ContainerFilters } from '~/compositions/containerFilters'
import { Appointment } from '~/models/appointments'

export function setAxiosAuthorizationHeader(header: string) {
  if (header === globalAxios.defaults.headers.common.Authorization) return
  globalAxios.defaults.headers.common.Authorization = header
}
export function clearAxiosAuthorizationHeader() {
  globalAxios.defaults.headers.common.Authorization = null
}
export function getAxiosAuthorizationHeader(): string | undefined {
  if (globalAxios.defaults.headers.common.Authorization) {
    return globalAxios.defaults.headers.common.Authorization.toString()
  }
  return undefined
}

export interface LoadContainerOptions extends ContainerFilters {
  abortController?: AbortController
  skipErrorHandling?: boolean
  pageSize: number
  page: number
  offset?: number
  preloadSize?: number
}

export interface LoadContainerOptionsWithPortComplexes extends LoadContainerOptions {
  port_complexes: PortComplex[]
}

function selectSortOption(
  state: ContainerWatchState | undefined
): SortOptions | undefined {
  switch (state) {
    case ContainerWatchState.ImportTrackingOnVessel:
      return SortOptions.VesselEta
    case ContainerWatchState.ImportTracking:
    case ContainerWatchState.ImportApptBooking:
      return SortOptions.LastFreeDay
    case ContainerWatchState.ImportApptBooked:
      return SortOptions.AppointmentTime // Factors in both import and empty appointments
    case ContainerWatchState.ImportPickedUp:
    case ContainerWatchState.ImportEmptyApptBooked:
      return SortOptions.ImportOutGatedTime
    case ContainerWatchState.ImportEmptyReturned:
      return SortOptions.EmptyInGatedTime
    default:
      return undefined
  }
}

export function loadContainers({
  terminal,
  container_numbers,
  excluded_customer_ids,
  auto_book_on,
  auto_book_off,
  assigned_user_ids,
  container_types,
  shipping_lines,
  hold_types,
  states,
  page,
  tags,
  show_not_tagged,
  is_green_truck,
  needs_line_lfd_check,
  vessels,
  vessel_eta,
  vessel_eta_is_unknown,
  pageSize,
  offset,
  updated_in_last_minutes,
  port_complexes,
  abortController,
  skipErrorHandling,
}: LoadContainerOptionsWithPortComplexes) {
  const api = new ContainersApi()
  let sortBy: SortOptions | undefined
  if (states && states?.length > 0) {
    sortBy = selectSortOption(states[0])
  }
  let sortOrder: 'ascending' | 'descending' = 'ascending'
  if (
    sortBy === SortOptions.ImportOutGatedTime ||
    sortBy === SortOptions.EmptyInGatedTime
  ) {
    sortOrder = 'descending'
  }
  return api.getContainersContainersGet(
    sortBy,
    sortOrder,
    page,
    pageSize,
    offset,
    terminal,
    states,
    undefined,
    undefined,
    vessels,
    vessel_eta,
    vessel_eta_is_unknown,
    undefined, // customer_ids — Imports hides customers instead
    excluded_customer_ids,
    undefined, // customer_id (singular)
    auto_book_on,
    auto_book_off,
    container_types,
    shipping_lines,
    hold_types,
    updated_in_last_minutes,
    tags,
    show_not_tagged,
    is_green_truck,
    needs_line_lfd_check,
    container_numbers,
    assigned_user_ids,
    port_complexes,
    { signal: abortController?.signal, skipErrorHandling }
  )
}

export interface LoadContainerBasicOptions {
  containers?: string
  states?: ContainerWatchState[]
  containerTypes?: ContainerType[]
  shippingLines?: ShippingLine[]
  assignedUserIds?: number[]
  importOutGatedAfter?: DateTime
  importOutGatedBefore?: DateTime
  limit?: number
  updatedInLastMinutes?: number
  tags?: string[]
  show_not_tagged?: boolean
  vessels?: string[]
  vessel_eta?: string
  vessel_eta_is_unknown?: boolean
}

export interface LoadContainerBasicOptionsWithPortComplexes extends LoadContainerBasicOptions {
  port_complexes: PortComplex[]
}

// `/containers/basic/` and `/containers/basic/with_unwatched/` take identical filters,
// so share the positional query args. Typed against the generated method so the spread
// stays in sync if the endpoint's params ever change.
function basicContainerQueryArgs(
  {
    containers,
    states,
    containerTypes,
    shippingLines,
    assignedUserIds,
    importOutGatedAfter,
    importOutGatedBefore,
    limit,
    updatedInLastMinutes,
    tags,
    show_not_tagged,
    vessels,
    vessel_eta,
    vessel_eta_is_unknown,
    port_complexes,
  }: LoadContainerBasicOptionsWithPortComplexes,
  requestOptions?: RawAxiosRequestConfig
): Parameters<ContainersApi['getContainersBasicContainersBasicGet']> {
  return [
    importOutGatedAfter ? importOutGatedAfter.toISO() : undefined,
    importOutGatedBefore ? importOutGatedBefore.toISO() : undefined,
    limit,
    states,
    undefined,
    undefined,
    vessels,
    vessel_eta,
    vessel_eta_is_unknown,
    undefined, // customer_ids
    undefined, // excluded_customer_ids — Empties hides customers client-side
    undefined, // customer_id (singular)
    undefined,
    undefined,
    containerTypes,
    shippingLines,
    undefined, // hold_types — Empties list does not filter by holds
    updatedInLastMinutes,
    tags,
    show_not_tagged,
    undefined, // is_green_truck — Empties list does not filter by green trucks
    undefined, // needs_line_lfd_check — an import-load concern, not an empties one
    containers,
    assignedUserIds,
    port_complexes,
    requestOptions,
  ]
}

export function loadContainersBasic(
  opts: LoadContainerBasicOptionsWithPortComplexes,
  requestOptions?: RawAxiosRequestConfig
) {
  return new ContainersApi().getContainersBasicContainersBasicGet(
    ...basicContainerQueryArgs(opts, requestOptions)
  )
}

// Fallback for containers /basic/ can't answer (e.g. an empty this org never tracked as
// an import). Searches every org, and returns only the line and type. At most 50 unique
// numbers per call; the manager that calls this does the chunking.
export function loadContainersLineAndType(
  containerNumbers: string[],
  config: RawAxiosRequestConfig
) {
  return new ContainersApi().getContainersLineAndTypeContainersLineAndTypeGet(
    containerNumbers.join(','),
    config
  )
}

// Like loadContainersBasic but returns { containers, unwatched_containers } — the
// searched container numbers this org doesn't track (feeds the Empties "Start tracking"
// banner/CTA) — and each container's empty auto-book status
// (auto_book_empty_request_status), so the Empties list gets rows + AUTO BOOK badges in
// one request instead of a second dependent /list/minimal/ call. Kept on a separate
// endpoint so /basic/'s bare-array contract is untouched.
export function loadContainersBasicWithUnwatchedAndAutoBook(
  opts: LoadContainerBasicOptionsWithPortComplexes
) {
  return new ContainersApi().getContainersBasicWithUnwatchedAndAutoBookContainersBasicWithUnwatchedWithAutoBookGet(
    ...basicContainerQueryArgs(opts)
  )
}

export interface LoadAppointmentCountsParams {
  time_bucket_hours: number
  after: DateTime
  before?: DateTime
  terminal_name?: TerminalName
  direction?: TransactionDirection
  loaded?: boolean
  customer_id?: number
  exclude_customer_ids?: string
  excludeContainerTags?: string[]
  containerTags?: string[]
  port_complexes?: PortComplex[]
}

export async function loadAppointmentCounts(
  params: LoadAppointmentCountsParams
): Promise<GETTimeBucketAppointmentStatusCount[]> {
  const {
    time_bucket_hours,
    after,
    before,
    terminal_name,
    direction,
    loaded,
    customer_id,
    exclude_customer_ids,
    excludeContainerTags,
    containerTags,
    port_complexes,
  } = params
  const api = new AppointmentsApi()
  const tz = DateTime.now().toFormat('z')
  const resp = await api.getAppointmentCountsAppointmentsCountsGet(
    time_bucket_hours,
    after.toUTC().toISO(),
    before?.toUTC().toISO(),
    terminal_name,
    direction,
    loaded,
    customer_id,
    containerTags,
    exclude_customer_ids,
    excludeContainerTags,
    tz,
    port_complexes
  )

  resp.data.sort((a, b) =>
    a.time_bucket_start > b.time_bucket_start
      ? 1
      : b.time_bucket_start > a.time_bucket_start
        ? -1
        : 0
  )

  return resp.data
}

export async function getEmptyAppointment(
  container_number: string,
  after: DateTime,
  terminal: TerminalName,
  terminal_reference: string
): Promise<Appointment | undefined> {
  const api = new AppointmentsApi()
  const resp = await api.listAppointmentsAppointmentsGet(
    terminal,
    undefined,
    after.toISO(),
    undefined,
    TransactionDirection.Inbound,
    false,
    undefined,
    undefined,
    undefined,
    undefined,
    container_number
  )

  if (resp.data.length === 0) {
    return undefined
  }
  // find the matching terminal_reference
  const matchingAppointment = resp.data.find(
    (appt) => appt.terminal_reference === terminal_reference
  )
  if (matchingAppointment) {
    return new Appointment(matchingAppointment, false)
  }
  return undefined
}
